import { ExperienceTagCount } from "redmond";

/**
 * Pinned categories to always show after attractions (if available).
 * These are matched by name (case-insensitive) against the category tags
 * returned from the backend.
 *
 * To modify which categories are pinned, update this array.
 * Categories are shown in the order listed here.
 */
export const PINNED_CATEGORY_NAMES = [
  "Food & Drink", // 21911
  "Nature and Wildlife Tours", // 11903,
  "On the Water", // 21442,
  "Private and Luxury", // 11938
] as const;

/**
 * Configuration for attraction vs category pill display logic.
 * These values are easily adjustable for experimentation.
 */
export const AVAILABILITY_FILTER_PILL_CONFIG = {
  /**
   * Maximum number of pills to display (excluding "Best of <destination>")
   */
  MAX_PILLS: 10,

  /**
   * Minimum number of attractions needed to show any attraction pills.
   * Below this threshold, only category pills are shown.
   * This will be experimented with, starting with 1, to see if we want to actually filter these out.
   */
  MIN_ATTRACTIONS_THRESHOLD: 1,

  /**
   * Number of top attractions to show regardless of product count.
   * These attractions are guaranteed to be shown (if they exist) without
   * checking MIN_PRODUCTS_PER_ATTRACTION.
   */
  NUM_TOP_ATTRACTIONS_GUARANTEED: 5,

  /**
   * Minimum number of products (activities) an attraction must have to be shown as a pill.
   * Only applies to attractions beyond NUM_TOP_ATTRACTIONS_GUARANTEED.
   * Attractions with fewer products will be excluded from remaining slots.
   */
  MIN_PRODUCTS_PER_ATTRACTION: 20,

  /**
   * Category names to pin at the end of the pills list (after attractions).
   * These are matched case-insensitively against backend category names.
   * Categories are shown in the order listed here.
   */
  PINNED_CATEGORIES: PINNED_CATEGORY_NAMES,
} as const;

/**
 * Configuration for the activity grid display.
 * Controls how many activities are shown initially and per "load more" action.
 */
export const AVAILABILITY_GRID_CONFIG = {
  /**
   * Number of activities to show initially and per "load more" click.
   * With a 4-column grid, 24 = 6 rows, 32 = 8 rows, 48 = 12 rows.
   */
  PAGE_SIZE: 24,
} as const;

export type FilterPillType = "Attraction" | "Category";

export interface AvailabilityFilterPill {
  /**
   * The tag count data (works for both attractions and categories)
   */
  tagCount: ExperienceTagCount;
  /**
   * Whether this pill represents an attraction or a category
   */
  type: FilterPillType;
}

/**
 * Finds pinned categories from the available category tags.
 * Returns them in the order specified in the config.
 */
function findPinnedCategories(
  categoryTags: ExperienceTagCount[],
  pinnedNames: readonly string[]
): ExperienceTagCount[] {
  const categoryByNameLower = new Map(
    categoryTags.map((cat) => [cat.tag.name.toLowerCase(), cat])
  );

  return pinnedNames
    .map((name) => categoryByNameLower.get(name.toLowerCase()))
    .filter((cat): cat is ExperienceTagCount => cat !== undefined);
}

/**
 * Determines which pills to display based on available attractions and categories.
 *
 * Logic:
 * - If < MIN_ATTRACTIONS_THRESHOLD total attractions: show only categories
 *   (prioritizing pinned categories first, then filling with others)
 * - If >= MIN_ATTRACTIONS_THRESHOLD attractions: show attractions first,
 *   then pinned categories to fill remaining slots
 * - First NUM_TOP_ATTRACTIONS_GUARANTEED attractions are shown regardless of product count
 * - Remaining attraction slots check MIN_PRODUCTS_PER_ATTRACTION threshold
 * - Always aim for MAX_PILLS total pills
 * - Pinned categories are shown in their configured order
 *
 * @param attractionTags - Attraction tags from the availability response (count = product count)
 * @param categoryTags - Category tags from the availability response
 * @param config - Optional configuration overrides
 * @returns Array of DisplayPill objects to render
 */
export function computeAvailabilityFilterPills(
  attractionTags: ExperienceTagCount[] | undefined,
  categoryTags: ExperienceTagCount[],
  config: typeof AVAILABILITY_FILTER_PILL_CONFIG = AVAILABILITY_FILTER_PILL_CONFIG
): AvailabilityFilterPill[] {
  const {
    MAX_PILLS,
    MIN_ATTRACTIONS_THRESHOLD,
    NUM_TOP_ATTRACTIONS_GUARANTEED,
    MIN_PRODUCTS_PER_ATTRACTION,
    PINNED_CATEGORIES,
  } = config;

  const allAttractions = attractionTags ?? [];

  // Find pinned categories that exist in the response
  const pinnedCategories = findPinnedCategories(categoryTags, PINNED_CATEGORIES);
  const pinnedCategoryIds = new Set(pinnedCategories.map((cat) => cat.tag.id));

  // Case 1: Not enough attractions - show pinned categories first, then other categories
  if (allAttractions.length < MIN_ATTRACTIONS_THRESHOLD) {
    const pinnedPills: AvailabilityFilterPill[] = pinnedCategories
      .slice(0, MAX_PILLS)
      .map((tagCount) => ({
        tagCount,
        type: "Category" as const,
      }));

    const remainingSlots = MAX_PILLS - pinnedPills.length;
    if (remainingSlots > 0) {
      const otherCategories = categoryTags.filter(
        (cat) => !pinnedCategoryIds.has(cat.tag.id)
      );
      const otherPills: AvailabilityFilterPill[] = otherCategories
        .slice(0, remainingSlots)
        .map((tagCount) => ({
          tagCount,
          type: "Category" as const,
        }));
      return [...pinnedPills, ...otherPills];
    }

    return pinnedPills;
  }

  // Case 2: Enough attractions - show attractions first, then pinned categories
  // Reserve slots for pinned categories that exist
  const slotsForPinnedCategories = Math.min(pinnedCategories.length, MAX_PILLS);
  const slotsForAttractions = MAX_PILLS - slotsForPinnedCategories;

  // First NUM_TOP_ATTRACTIONS_GUARANTEED attractions are shown regardless of product count
  const guaranteedSlots = Math.min(NUM_TOP_ATTRACTIONS_GUARANTEED, slotsForAttractions);
  const guaranteedAttractions = allAttractions.slice(0, guaranteedSlots);

  // Remaining attraction slots check MIN_PRODUCTS_PER_ATTRACTION
  const remainingAttractionSlots = slotsForAttractions - guaranteedAttractions.length;
  const remainingAttractions = allAttractions.slice(guaranteedSlots);
  const qualifyingRemainingAttractions = remainingAttractions.filter(
    (attraction) => attraction.count >= MIN_PRODUCTS_PER_ATTRACTION
  );

  // Combine guaranteed + qualifying remaining, limited to available slots
  const attractionsToShow = [
    ...guaranteedAttractions,
    ...qualifyingRemainingAttractions.slice(0, remainingAttractionSlots),
  ];

  const attractionPills: AvailabilityFilterPill[] = attractionsToShow.map(
    (tagCount) => ({
      tagCount,
      type: "Attraction" as const,
    })
  );

  // Fill remaining slots with pinned categories first
  const remainingSlotsAfterAttractions = MAX_PILLS - attractionPills.length;
  const pinnedCategoryPills: AvailabilityFilterPill[] = pinnedCategories
    .slice(0, remainingSlotsAfterAttractions)
    .map((tagCount) => ({
      tagCount,
      type: "Category" as const,
    }));

  // If still not at MAX_PILLS, backfill with other (non-pinned) categories
  const currentTotal = attractionPills.length + pinnedCategoryPills.length;
  const backfillSlots = MAX_PILLS - currentTotal;

  let otherCategoryPills: AvailabilityFilterPill[] = [];
  if (backfillSlots > 0) {
    const otherCategories = categoryTags.filter(
      (cat) => !pinnedCategoryIds.has(cat.tag.id)
    );
    otherCategoryPills = otherCategories
      .slice(0, backfillSlots)
      .map((tagCount) => ({
        tagCount,
        type: "Category" as const,
      }));
  }

  return [...attractionPills, ...pinnedCategoryPills, ...otherCategoryPills];
}

/**
 * Checks if attractions should be used for pill display.
 * Useful for determining if attraction-specific UI should be shown.
 * Returns true if there are >= MIN_ATTRACTIONS_THRESHOLD total attractions.
 */
export function shouldShowAttractions(
  attractionTags: ExperienceTagCount[] | undefined,
  config: typeof AVAILABILITY_FILTER_PILL_CONFIG = AVAILABILITY_FILTER_PILL_CONFIG
): boolean {
  if (!attractionTags || attractionTags.length === 0) {
    return false;
  }

  return attractionTags.length >= config.MIN_ATTRACTIONS_THRESHOLD;
}
